import { useState } from 'react';

export default function WorkProcess() {
  const [activePhase, setActivePhase] = useState<number>(0);

  const phases = [
    {
      number: '01',
      title: 'Consulta Inicial',
      subtitle: 'Análisis de Necesidades',
      description: 'Reunión con el cliente para entender sus objetivos, procesos actuales y necesidades específicas de automatización.',
      icon: 'ri-chat-4-line',
      duration: '1-2 días'
    },
    {
      number: '02',
      title: 'Estudio de Viabilidad',
      subtitle: 'Diseño Conceptual',
      description: 'Análisis técnico y económico del proyecto. Creación de bocetos y propuesta de solución personalizada con presupuesto detallado.',
      icon: 'ri-draft-line',
      duration: '1-2 semanas'
    },
    {
      number: '03',
      title: 'Ingeniería',
      subtitle: 'Desarrollo Técnico',
      description: 'Diseño detallado de la máquina, selección de componentes, programación de sistemas de control y desarrollo de software con IA.',
      icon: 'ri-code-box-line',
      duration: '3-6 semanas'
    },
    {
      number: '04',
      title: 'Fabricación',
      subtitle: 'Ensamblaje',
      description: 'Construcción de la máquina con componentes de alta calidad. Montaje de sistemas mecánicos, eléctricos y robóticos.',
      icon: 'ri-tools-line',
      duration: '4-8 semanas'
    },
    {
      number: '05',
      title: 'Pruebas',
      subtitle: 'Validación',
      description: 'Testing exhaustivo de todos los sistemas. Ajustes finos, calibración y optimización del rendimiento antes de la entrega.',
      icon: 'ri-checkbox-circle-line',
      duration: '1-2 semanas'
    },
    {
      number: '06',
      title: 'Instalación',
      subtitle: 'Entrega y Formación',
      description: 'Instalación en las instalaciones del cliente, puesta en marcha y formación completa del equipo para el uso óptimo de la máquina.',
      icon: 'ri-rocket-line',
      duration: '3-5 días'
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4">
            Nuestro Proceso{' '}
            <span className="inline-flex w-10 h-10 md:w-12 md:h-12 items-center justify-center">
              <i className="ri-flow-chart text-orange-500 text-2xl md:text-3xl"></i>
            </span>{' '}
            de Trabajo
          </h2>
          <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto">
            De la idea a la realidad: así transformamos tu visión en una máquina robótica de alto rendimiento
          </p>
        </div>

        {/* Timeline for desktop */}
        <div className="hidden md:block relative">
          {/* Connection line */}
          <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-green-500 to-orange-500"></div>

          <div className="grid grid-cols-3 gap-8 mb-12">
            {phases.slice(0, 3).map((phase, index) => (
              <div
                key={index}
                className="relative cursor-pointer group"
                onMouseEnter={() => setActivePhase(index)}
              >
                {/* Circle on line */}
                <div className={`absolute top-20 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-4 border-gray-900 transition-all duration-300 ${
                  activePhase === index ? 'bg-gradient-to-br from-orange-500 to-green-500 scale-125' : 'bg-gray-700'
                }`}></div>

                {/* Card */}
                <div className={`bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border-2 transition-all duration-300 ${
                  activePhase === index ? 'border-orange-500 shadow-xl shadow-orange-500/20 -translate-y-2' : 'border-gray-700'
                }`}>
                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl mb-4 transition-all duration-300 ${
                    activePhase === index ? 'bg-gradient-to-br from-orange-500 to-green-500' : 'bg-gray-700'
                  }`}>
                    <i className={`${phase.icon} text-white text-2xl`}></i>
                  </div>
                  
                  <div className="text-4xl font-black text-orange-500 mb-2">{phase.number}</div>
                  <h3 className="text-xl font-bold text-white mb-1">{phase.title}</h3>
                  <p className="text-sm text-green-400 mb-3">{phase.subtitle}</p>
                  <p className="text-sm text-gray-300 mb-3 leading-relaxed">{phase.description}</p>
                  <div className="flex items-center text-xs text-gray-400">
                    <i className="ri-time-line mr-1"></i>
                    {phase.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-8">
            {phases.slice(3, 6).map((phase, index) => (
              <div
                key={index + 3}
                className="relative cursor-pointer group"
                onMouseEnter={() => setActivePhase(index + 3)}
              >
                {/* Circle on line */}
                <div className={`absolute -top-5 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-4 border-gray-900 transition-all duration-300 ${
                  activePhase === index + 3 ? 'bg-gradient-to-br from-orange-500 to-green-500 scale-125' : 'bg-gray-700'
                }`}></div>

                {/* Card */}
                <div className={`bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border-2 transition-all duration-300 ${
                  activePhase === index + 3 ? 'border-green-500 shadow-xl shadow-green-500/20 -translate-y-2' : 'border-gray-700'
                }`}>
                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl mb-4 transition-all duration-300 ${
                    activePhase === index + 3 ? 'bg-gradient-to-br from-orange-500 to-green-500' : 'bg-gray-700'
                  }`}>
                    <i className={`${phase.icon} text-white text-2xl`}></i>
                  </div>
                  
                  <div className="text-4xl font-black text-orange-500 mb-2">{phase.number}</div>
                  <h3 className="text-xl font-bold text-white mb-1">{phase.title}</h3>
                  <p className="text-sm text-green-400 mb-3">{phase.subtitle}</p>
                  <p className="text-sm text-gray-300 mb-3 leading-relaxed">{phase.description}</p>
                  <div className="flex items-center text-xs text-gray-400">
                    <i className="ri-time-line mr-1"></i>
                    {phase.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile version */}
        <div className="md:hidden space-y-6">
          {phases.map((phase, index) => (
            <div
              key={index}
              className="relative"
              onClick={() => setActivePhase(index)}
            >
              {/* Vertical line */}
              {index < phases.length - 1 && (
                <div className="absolute left-7 top-16 bottom-0 w-1 bg-gradient-to-b from-orange-500 to-green-500 -mb-6"></div>
              )}

              {/* Card */}
              <div className={`bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border-2 transition-all duration-300 ${
                activePhase === index ? 'border-orange-500 shadow-xl shadow-orange-500/20' : 'border-gray-700'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 ${
                    activePhase === index ? 'bg-gradient-to-br from-orange-500 to-green-500' : 'bg-gray-700'
                  }`}>
                    <i className={`${phase.icon} text-white text-xl`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <div className="text-2xl font-black text-orange-500 mb-1">{phase.number}</div>
                    <h3 className="text-lg font-bold text-white mb-1">{phase.title}</h3>
                    <p className="text-sm text-green-400 mb-2">{phase.subtitle}</p>
                    <p className="text-sm text-gray-300 mb-2 leading-relaxed">{phase.description}</p>
                    <div className="flex items-center text-xs text-gray-400">
                      <i className="ri-time-line mr-1"></i>
                      {phase.duration}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 md:mt-16">
          <p className="text-base md:text-lg text-gray-300 mb-6">
            ¿Listo para comenzar tu proyecto de automatización?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-green-500 text-white px-8 py-4 rounded-full font-bold text-base md:text-lg hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Solicitar Consulta Gratuita
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
